import { useCallback, useEffect, useState } from "react"
import { CreateOpportunitiesModalProps } from "./types"
import type { IModalProps } from "../../basics/Modal/types"
import { Modal, Typography, TextInput, Button, Stack, Grid } from "src/lib"
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    type ModalProps,
    type SxProps,
    type Theme,
} from "@mui/material"
import {
    commitObjectiveDraft,
    createObjectiveDraft,
    regenerateObjectiveDraft,
} from "src/services/api/opportunities"
import type { ObjectiveDraftResponse } from "src/services/api/opportunities/draftTypes"
import {
    impactedClientColumnLabel,
    orderImpactedClientColumnsFromRows,
    formatImpactedClientCell,
} from "src/services/api/opportunities/impactedClientColumns"
import { intl } from "src/internationalization/intl"
import { useOpportunitiesStore } from "src/store"
import { useSnackbarStore } from "src/store/snackbarStore"

type Step = "form" | "review"

const mutedHelperSx: SxProps<Theme> = {
    color: (theme) => (theme as any).palette.neutral.main,
}

const inputTextSx: SxProps<Theme> = {
    "& .MuiOutlinedInput-input": {
        color: (theme) => (theme as any).palette.neutral.lighter,
    },
}

const todayIsoLocal = (): string => {
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, "0")
    const d = String(now.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
}

const addDaysIso = (isoDate: string, days: number): string => {
    const base = new Date(`${isoDate}T12:00:00`)
    base.setDate(base.getDate() + days)
    const y = base.getFullYear()
    const m = String(base.getMonth() + 1).padStart(2, "0")
    const d = String(base.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
}

/** Janela válida = início no passado (ou hoje) e fim depois do início.
 *
 * Uma janela inteiramente no passado é permitida: com a cypherQuery ancorada em
 * $createdDate, registrar uma oportunidade histórica fechada é bem definido. Já o
 * início futuro não teria posição para ancorar. Espelha o validador do backend.
 */
const validateObjectiveWindow = (
    createdDate: string,
    endDate: string,
): "ok" | "future" | "endBeforeStart" => {
    if (!createdDate || !endDate) return "ok"
    if (createdDate > todayIsoLocal()) return "future"
    if (endDate <= createdDate) return "endBeforeStart"
    return "ok"
}

const emptyForm = () => ({
    title: "",
    text: "",
    createdDate: todayIsoLocal(),
    endDate: addDaysIso(todayIsoLocal(), 30),
})

/** MUI dismissals we ignore so the user must use the modal close control. */
const isIncidentalModalDismissal = (reason?: string) =>
    reason === "backdropClick" || reason === "escapeKeyDown"

const CreateOpportunitiesModal = ({ open, onClose, ...rest }: CreateOpportunitiesModalProps) => {
    const [step, setStep] = useState<Step>("form")
    const [title, setTitle] = useState("")
    const [text, setText] = useState("")
    const [createdDate, setCreatedDate] = useState(todayIsoLocal)
    const [endDate, setEndDate] = useState(() => addDaysIso(todayIsoLocal(), 30))
    const [draft, setDraft] = useState<ObjectiveDraftResponse | null>(null)
    const [reviewStrategyText, setReviewStrategyText] = useState("")
    const [loadingDraft, setLoadingDraft] = useState(false)
    const [loadingRegenerate, setLoadingRegenerate] = useState(false)
    const [loadingCommit, setLoadingCommit] = useState(false)
    const triggerRefresh = useOpportunitiesStore((s: any) => s.triggerRefresh)
    const showSnackbar = useSnackbarStore((s) => s.showSnackbar)

    const handleClose = useCallback<NonNullable<ModalProps["onClose"]>>(
        (_event, reason) => {
            if (isIncidentalModalDismissal(reason)) return
            onClose()
        },
        [onClose]
    )

    useEffect(() => {
        if (!open) {
            const e = emptyForm()
            setStep("form")
            setTitle(e.title)
            setText(e.text)
            setCreatedDate(e.createdDate)
            setEndDate(e.endDate)
            setDraft(null)
            setReviewStrategyText("")
            setLoadingDraft(false)
            setLoadingRegenerate(false)
            setLoadingCommit(false)
        }
    }, [open])

    const modalTitle =
        step === "form" ? intl("objectiveDraftModalTitle") : intl("objectiveDraftReviewTitle")

    const dateWindowStatus = validateObjectiveWindow(createdDate, endDate)
    const formValid =
        text.trim().length > 0 && Boolean(createdDate) && Boolean(endDate) && dateWindowStatus === "ok"
    const startDateError =
        dateWindowStatus === "future" ? intl("objectiveStartDateFutureError") : ""
    const endDateError =
        dateWindowStatus === "endBeforeStart" ? intl("objectiveEndDateBeforeStartError") : ""

    const applyDraftResponse = (res: ObjectiveDraftResponse) => {
        setDraft(res)
        setReviewStrategyText(res.objective?.text ?? "")
    }

    const handleGenerateDraft = async () => {
        if (!formValid) return
        setLoadingDraft(true)
        try {
            const body = {
                ...(title.trim() ? { title: title.trim() } : {}),
                text: text.trim(),
                createdDate,
                endDate,
            }
            const res = await createObjectiveDraft(body)
            applyDraftResponse(res)
            setStep("review")
        } catch {
            // apiFetch já mostra snackbar
        } finally {
            setLoadingDraft(false)
        }
    }

    const handleRegenerate = async () => {
        if (!draft?.draft_id || !reviewStrategyText.trim()) return
        setLoadingRegenerate(true)
        try {
            const res = await regenerateObjectiveDraft({
                draft_id: draft.draft_id,
                text: reviewStrategyText.trim(),
            })
            applyDraftResponse(res)
        } catch {
            setStep("form")
            setDraft(null)
        } finally {
            setLoadingRegenerate(false)
        }
    }

    const handleCommit = async () => {
        if (!draft?.draft_id) return
        setLoadingCommit(true)
        try {
            const res = await commitObjectiveDraft({ draft_id: draft.draft_id })
            showSnackbar(res.message || intl("objectiveCommitSuccess"), "success")
            triggerRefresh()
            onClose()
        } catch {
            setStep("form")
            setDraft(null)
        } finally {
            setLoadingCommit(false)
        }
    }

    const handleBackToForm = () => {
        if (draft?.objective) {
            setTitle(draft.objective.title ?? "")
            setText(draft.objective.text ?? "")
            setCreatedDate(draft.objective.createdDate ?? todayIsoLocal())
            setEndDate(draft.objective.endDate ?? addDaysIso(todayIsoLocal(), 30))
        }
        setDraft(null)
        setStep("form")
    }

    const previewColumns =
        draft?.impacted_preview?.length
            ? orderImpactedClientColumnsFromRows(
                  draft.impacted_preview as Record<string, unknown>[]
              )
            : []

    const expiresLabel = draft?.expires_at
        ? new Date(draft.expires_at).toLocaleString("pt-BR", {
              dateStyle: "short",
              timeStyle: "short",
          })
        : ""

    return (
        <Modal
            open={open}
            onClose={handleClose as IModalProps["onClose"]}
            {...rest}
            title={modalTitle}
        >
            <Stack spacing={3} sx={{ maxHeight: "72vh", overflow: "auto", pr: 0.5 }}>
                {step === "form" && (
                    <>
                        <Typography variant="body2" sx={mutedHelperSx}>
                            {intl("objectiveDraftIntro")}
                        </Typography>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveReportTitleLabel")}
                            </Typography>
                            <TextInput
                                fullWidth
                                placeholder="Ex.: Oportunidade renda fixa — abril"
                                size="medium"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                sx={inputTextSx}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {intl("objectivePromptLabel")}
                                <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                                    *
                                </Box>
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block", mb: 1, ...mutedHelperSx }}>
                                {intl("objectivePromptHelper")}
                            </Typography>
                            <TextInput
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={14}
                                placeholder="Ex.: Aumentar exposição em inflação até o limite da carteira de referência dos perfis"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                sx={inputTextSx}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {intl("objectiveStartDateLabel")}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block", mb: 1, ...mutedHelperSx }}>
                                {intl("objectiveStartDateHelper")}
                            </Typography>
                            <TextInput
                                type="date"
                                fullWidth
                                value={createdDate}
                                onChange={(e) => setCreatedDate(e.target.value)}
                                inputProps={{ max: todayIsoLocal() }}
                                error={Boolean(startDateError)}
                                helperText={startDateError}
                                sx={{ ...inputTextSx, maxWidth: 280 }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("dueDate")}
                                <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                                    *
                                </Box>
                            </Typography>
                            <TextInput
                                type="date"
                                fullWidth
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                inputProps={{ min: createdDate || undefined }}
                                error={Boolean(endDateError)}
                                helperText={endDateError}
                                sx={{ ...inputTextSx, maxWidth: 280 }}
                            />
                        </Box>

                        <Grid container justifyContent="flex-end">
                            <Button
                                variant="contained"
                                onClick={handleGenerateDraft}
                                disabled={!formValid || loadingDraft}
                            >
                                {loadingDraft ? <CircularProgress size={22} /> : intl("objectiveGenerateDraftButton")}
                            </Button>
                        </Grid>
                    </>
                )}

                {step === "review" && draft && (
                    <>
                        <Typography variant="body2" fontWeight={600}>
                            {intl("objectiveReviewHeading")}
                        </Typography>
                        {expiresLabel && (
                            <Typography variant="caption" sx={mutedHelperSx}>
                                {intl("objectiveExpiresHint")}: {expiresLabel}
                            </Typography>
                        )}

                        {draft.can_commit === false && (
                            <Typography variant="body2" sx={{ mb: 1, color: "error.main" }}>
                                {draft.warnings?.[0] || intl("objectiveDraftCannotCommit")}
                            </Typography>
                        )}

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveInterpretationHeading")}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: (t) => (t as any).palette?.neutral?.darker ?? "action.hover",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                <Typography variant="body2">{draft.interpretation || "—"}</Typography>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveImpactedCount")}:{" "}
                                <Typography component="span" variant="body2" fontWeight={600}>
                                    {draft.impacted_count ?? 0}
                                </Typography>
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveImpactedPreviewHeading")}
                            </Typography>
                            {!draft.impacted_preview?.length ? (
                                <Typography variant="body2" sx={mutedHelperSx}>
                                    {intl("objectivePreviewEmpty")}
                                </Typography>
                            ) : (
                                <Table
                                    size="small"
                                    sx={{
                                        minWidth: 520,
                                        "& .MuiTableCell-root": {
                                            color: (t) => (t as any).palette.neutral.lighter,
                                            borderColor: (t) => (t as any).palette.neutral.dark,
                                        },
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {previewColumns.map((col) => (
                                                <TableCell key={col}>
                                                    {impactedClientColumnLabel(col)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {draft.impacted_preview.map((row, ri) => (
                                            <TableRow key={ri}>
                                                {previewColumns.map((col) => (
                                                    <TableCell key={col}>
                                                        {formatImpactedClientCell(col, row[col])}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {intl("objectiveStrategyAdjustLabel")}
                            </Typography>
                            <TextInput
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={12}
                                value={reviewStrategyText}
                                onChange={(e) => setReviewStrategyText(e.target.value)}
                                sx={inputTextSx}
                            />
                        </Box>

                        <Grid container justifyContent="space-between" gap={2} flexWrap="wrap">
                            <Button variant="outlined" color="neutral" onClick={handleBackToForm}>
                                {intl("objectiveBackToFormButton")}
                            </Button>
                            <Grid container gap={1} flexWrap="wrap" justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={handleRegenerate}
                                    disabled={
                                        !reviewStrategyText.trim() || loadingRegenerate || loadingCommit
                                    }
                                >
                                    {loadingRegenerate ? (
                                        <CircularProgress size={22} />
                                    ) : (
                                        intl("objectiveRegenerateButton")
                                    )}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleCommit}
                                    disabled={
                                        loadingCommit ||
                                        loadingRegenerate ||
                                        draft.can_commit === false
                                    }
                                >
                                    {loadingCommit ? (
                                        <CircularProgress size={22} />
                                    ) : (
                                        intl("objectiveCommitButton")
                                    )}
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Stack>
        </Modal>
    )
}

export { CreateOpportunitiesModal }
